import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Trash2, TriangleAlert } from "lucide-react";
import adminApi, { apiError } from "@/lib/adminApi";
import { Panel, btnPrimary, inputCls } from "@/components/admin/ui";

const CATEGORIES = [["business", "Business Class"], ["first", "First Class"], ["van", "VAN"], ["standard", "Standard Class"]];

const Field = ({ label, value, onChange, testId, mono = false, placeholder = "", type = "text" }) => (
  <div>
    <label className="text-xs font-medium text-slate">{label}</label>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
      placeholder={placeholder} data-testid={testId} className={`${inputCls} mt-1 ${mono ? "font-mono" : ""}`} />
  </div>
);

export default function AdminSettings() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [promos, setPromos] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", percent_off: "", max_uses: "" });
  const [promoBusy, setPromoBusy] = useState(false);

  const loadPromos = () => adminApi.get("/promo-codes").then((r) => setPromos(r.data.promo_codes || [])).catch(() => {});

  const createPromo = async () => {
    if (promoBusy || !promoForm.code.trim() || !promoForm.percent_off) return;
    setPromoBusy(true);
    try {
      await adminApi.post("/promo-codes", {
        code: promoForm.code.trim(),
        percent_off: Number(promoForm.percent_off),
        max_uses: promoForm.max_uses ? Number(promoForm.max_uses) : null,
      });
      setPromoForm({ code: "", percent_off: "", max_uses: "" });
      loadPromos();
      toast.success("Code promo créé — actif immédiatement sur le site.");
    } catch (err) {
      toast.error(apiError(err, "Création impossible"));
    } finally { setPromoBusy(false); }
  };

  const togglePromo = async (promo) => {
    try {
      await adminApi.patch(`/promo-codes/${promo.id}`, { active: !promo.active });
      loadPromos();
    } catch (err) { toast.error(apiError(err, "Erreur")); }
  };

  const deletePromo = async (promo) => {
    try {
      await adminApi.delete(`/promo-codes/${promo.id}`);
      loadPromos();
      toast.success(`Code ${promo.code} supprimé.`);
    } catch (err) { toast.error(apiError(err, "Suppression impossible")); }
  };

  const resetData = async () => {
    if (resetting) return;
    setResetting(true);
    try {
      const r = await adminApi.post("/maintenance/reset-data", { confirm: "RESET" });
      setResetResult(r.data.total);
      setResetConfirm("");
    } catch (err) {
      toast.error(apiError(err, "Remise à zéro impossible"));
    } finally {
      setResetting(false);
    }
  };

  const load = () => adminApi.get("/settings").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); loadPromos(); }, []);

  if (!data) return <div className="h-96 animate-pulse rounded-md border border-slate/15 bg-white" />;
  const s = data.settings;

  const setSection = (section, key, value) => {
    setData((d) => ({ ...d, settings: { ...d.settings, [section]: { ...d.settings[section], [key]: value } } }));
  };
  const setPricing = (cat, key, value) => {
    setData((d) => ({ ...d, pricing: { ...d.pricing, [cat]: { ...d.pricing[cat], [key]: value } } }));
  };
  const setHourly = (key, value) => {
    setData((d) => ({ ...d, hourly: { ...d.hourly, [key]: value } }));
  };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await adminApi.patch("/settings", { company: s.company, bank: s.bank, billing: s.billing, pricing: data.pricing, hourly: data.hourly });
      setData((d) => ({ ...d, billing_validation: r.data.billing_validation }));
      toast.success("Paramètres enregistrés — appliqués immédiatement.");
    } catch (error) {
      toast.error(apiError(error, "Erreur"));
    } finally { setBusy(false); }
  };

  const validation = data.billing_validation || [];
  const iban = data.iban_info || {};

  return (
    <div className="mx-auto max-w-4xl space-y-4" data-testid="admin-settings">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Réglages</h1>
        <button type="button" onClick={save} disabled={busy} className={btnPrimary} data-testid="settings-save">
          {busy ? "Enregistrement…" : "Enregistrer tout"}
        </button>
      </div>

      <Panel title="Société" testId="settings-company">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Nom commercial" value={s.company.brand_name} onChange={(v) => setSection("company", "brand_name", v)} testId="company-brand" />
          <Field label="Raison sociale légale" value={s.company.legal_name} onChange={(v) => setSection("company", "legal_name", v)} testId="company-legal" />
          <Field label="Titulaire bancaire exact" value={s.bank.account_holder} onChange={(v) => setSection("bank", "account_holder", v)} testId="bank-holder" placeholder="Tel qu'enregistré à la banque" />
          <Field label="E-mail comptabilité" value={s.company.accounting_email} onChange={(v) => setSection("company", "accounting_email", v)} testId="company-accounting" placeholder="comptabilité@gomiles.ch" />
          <Field label="E-mail" value={s.company.email} onChange={(v) => setSection("company", "email", v)} testId="company-email" />
          <Field label="Téléphone" value={s.company.phone} onChange={(v) => setSection("company", "phone", v)} testId="company-phone" />
          <Field label="Site internet" value={s.company.website} onChange={(v) => setSection("company", "website", v)} testId="company-website" />
          <Field label="N° IDE / UID" value={s.company.ide_number} onChange={(v) => setSection("company", "ide_number", v)} testId="company-ide" mono placeholder="CHE-000.000.000" />
          <Field label="N° TVA" value={s.company.vat_number} onChange={(v) => setSection("company", "vat_number", v)} testId="company-vat" mono placeholder="CHE-000.000.000 TVA" />
        </div>
      </Panel>

      <Panel title="Facturation & Banque" testId="settings-bank"
        action={validation.length === 0 ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600" data-testid="billing-valid">
            <ShieldCheck className="h-4 w-4" strokeWidth={1.5} /> QR-facture prête</span>
        ) : null}>
        {validation.length > 0 && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4" data-testid="billing-errors">
            <p className="flex items-center gap-2 text-sm font-semibold text-red-700">
              <TriangleAlert className="h-4 w-4" strokeWidth={2} /> Impossible de générer la QR-facture</p>
            <ul className="mt-1.5 list-inside list-disc space-y-0.5 text-xs text-red-700">
              {validation.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="IBAN" value={s.bank.iban} onChange={(v) => setSection("bank", "iban", v)} testId="bank-iban" mono placeholder="CH43 0024 5245 2203 7940 R" />
          <Field label="QR-IBAN (si fourni par la banque)" value={s.bank.qr_iban} onChange={(v) => setSection("bank", "qr_iban", v)} testId="bank-qriban" mono placeholder="Laisser vide si IBAN classique" />
          <Field label="BIC / SWIFT (optionnel)" value={s.bank.bic} onChange={(v) => setSection("bank", "bic", v)} testId="bank-bic" mono />
          <div className="grid grid-cols-[1fr_80px] gap-3">
            <Field label="Rue" value={s.bank.street} onChange={(v) => setSection("bank", "street", v)} testId="bank-street" />
            <Field label="N°" value={s.bank.house_num} onChange={(v) => setSection("bank", "house_num", v)} testId="bank-num" />
          </div>
          <div className="grid grid-cols-[90px_1fr_64px] gap-3 sm:col-span-2">
            <Field label="NPA" value={s.bank.pcode} onChange={(v) => setSection("bank", "pcode", v)} testId="bank-pcode" mono />
            <Field label="Localité" value={s.bank.city} onChange={(v) => setSection("bank", "city", v)} testId="bank-city" />
            <Field label="Pays" value={s.bank.country} onChange={(v) => setSection("bank", "country", v)} testId="bank-country" />
          </div>
        </div>
        <p className="mt-3 rounded-lg bg-cloud px-3 py-2 text-xs text-slate" data-testid="iban-info">
          {iban.present ? (iban.valid
            ? `IBAN ${iban.formatted} — ${iban.is_qr_iban ? "QR-IBAN détecté → références QRR" : "IBAN classique → références SCOR (ISO 11649)"}`
            : "IBAN invalide — corrigez-le avant d&apos;émettre des factures")
            : "Aucun IBAN enregistré."}
        </p>
      </Panel>

      <Panel title="Paramètres de facturation" testId="settings-billing">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="flex items-center gap-3 rounded-xl border border-ink/[0.07] p-3">
            <input type="checkbox" id="vat-reg" checked={s.billing.vat_registered}
              onChange={(e) => setSection("billing", "vat_registered", e.target.checked)} data-testid="billing-vat-registered"
              className="h-4 w-4 accent-miles" />
            <label htmlFor="vat-reg" className="text-sm font-medium text-ink">Entreprise assujettie à la TVA</label>
          </div>
          <Field label="Taux TVA par défaut (%)" type="number" value={s.billing.vat_rate} onChange={(v) => setSection("billing", "vat_rate", v)} testId="billing-vat-rate" mono />
          <Field label="Devise" value={s.billing.currency} onChange={(v) => setSection("billing", "currency", v)} testId="billing-currency" mono placeholder="CHF" />
          <Field label="Préfixe factures" value={s.billing.invoice_prefix} onChange={(v) => setSection("billing", "invoice_prefix", v)} testId="billing-inv-prefix" mono />
          <Field label="Préfixe devis" value={s.billing.quote_prefix} onChange={(v) => setSection("billing", "quote_prefix", v)} testId="billing-quo-prefix" mono />
          <Field label="Préfixe avoirs" value={s.billing.credit_note_prefix} onChange={(v) => setSection("billing", "credit_note_prefix", v)} testId="billing-cn-prefix" mono />
          <Field label="Délai de paiement (jours)" type="number" value={s.billing.payment_terms_days} onChange={(v) => setSection("billing", "payment_terms_days", v)} testId="billing-terms" mono />
          <Field label="Validité des devis (jours)" type="number" value={s.billing.quote_validity_days} onChange={(v) => setSection("billing", "quote_validity_days", v)} testId="billing-validity" mono />
        </div>
        <div className="mt-3">
          <Field label="Mention légale (pied de page des documents)" value={s.billing.legal_note} onChange={(v) => setSection("billing", "legal_note", v)} testId="billing-legal" />
        </div>
      </Panel>

      <Panel title="Tarification (moteur central)" testId="settings-pricing">
        <p className="mb-4 text-sm text-slate">Ces règles alimentent immédiatement les estimations du site public, de MILES Business et de l&apos;admin.</p>
        <div className="space-y-3">
          {CATEGORIES.map(([key, label]) => (
            <div key={key} className="grid grid-cols-2 items-end gap-3 rounded border border-slate/15 p-3 sm:grid-cols-5" data-testid={`pricing-${key}`}>
              <p className="col-span-2 text-sm font-semibold text-ink sm:col-span-1">{label}</p>
              {[["min_fare", "Minimum CHF"], ["per_km", "Par km"], ["per_hour", "Par heure"], ["extra_km", "Km suppl. (horaire)"]].map(([field, flabel]) => (
                <div key={field}>
                  <label className="text-xs font-medium text-slate">{flabel}</label>
                  <input type="number" step="0.05" value={data.pricing[key][field]} onChange={(e) => setPricing(key, field, e.target.value)}
                    data-testid={`pricing-${key}-${field}`} className={`${inputCls} mt-1 font-mono`} />
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="mt-4 rounded border border-slate/15 p-3" data-testid="pricing-hourly">
          <p className="text-sm font-semibold text-ink">Prestation à l&apos;heure — conditions</p>
          <p className="mt-0.5 text-xs text-slate">Durées proposées aux clients et kilométrage inclus par heure (le km supplémentaire est tarifé par véhicule ci-dessus).</p>
          <div className="mt-3 grid grid-cols-3 gap-3">
            {[["min_hours", "Durée min (h)"], ["max_hours", "Durée max (h)"], ["km_per_hour", "Km inclus / heure"]].map(([field, flabel]) => (
              <div key={field}>
                <label className="text-xs font-medium text-slate">{flabel}</label>
                <input type="number" step="1" min="1" value={data.hourly?.[field] ?? ""} onChange={(e) => setHourly(field, e.target.value)}
                  data-testid={`hourly-${field}`} className={`${inputCls} mt-1 font-mono`} />
              </div>
            ))}
          </div>
        </div>
      </Panel>

      <Panel title="Codes promo" testId="settings-promos">
        <p className="mb-4 text-sm text-slate">Codes de réduction utilisables par les clients à l&apos;étape paiement du site public.</p>
        <div className="grid grid-cols-2 items-end gap-3 sm:grid-cols-[1fr_120px_140px_auto]">
          <div>
            <label className="text-xs font-medium text-slate">Code</label>
            <input value={promoForm.code} onChange={(e) => setPromoForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="BIENVENUE10" data-testid="promo-new-code" className={`${inputCls} mt-1 font-mono uppercase`} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate">Réduction %</label>
            <input type="number" min="1" max="100" value={promoForm.percent_off}
              onChange={(e) => setPromoForm((f) => ({ ...f, percent_off: e.target.value }))}
              placeholder="10" data-testid="promo-new-percent" className={`${inputCls} mt-1 font-mono`} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate">Utilisations max (optionnel)</label>
            <input type="number" min="1" value={promoForm.max_uses}
              onChange={(e) => setPromoForm((f) => ({ ...f, max_uses: e.target.value }))}
              placeholder="Illimité" data-testid="promo-new-max" className={`${inputCls} mt-1 font-mono`} />
          </div>
          <button type="button" onClick={createPromo} disabled={promoBusy || !promoForm.code.trim() || !promoForm.percent_off}
            className={btnPrimary} data-testid="promo-create">Créer</button>
        </div>
        <div className="mt-4 space-y-2" data-testid="promo-list">
          {promos.length === 0 && <p className="text-xs text-slate" data-testid="promo-empty">Aucun code promo pour l&apos;instant.</p>}
          {promos.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 rounded border border-slate/15 px-3 py-2.5" data-testid={`promo-row-${p.code}`}>
              <div className="min-w-0">
                <p className="font-mono text-sm font-bold text-ink">{p.code} <span className="ml-1 font-sans text-xs font-semibold text-miles">−{p.percent_off}%</span></p>
                <p className="text-[11px] text-slate">
                  {p.times_used || 0} utilisation{(p.times_used || 0) > 1 ? "s" : ""}{p.max_uses ? ` / ${p.max_uses} max` : " · illimité"}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button type="button" onClick={() => togglePromo(p)} data-testid={`promo-toggle-${p.code}`}
                  className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${p.active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-slate/10 text-slate hover:bg-slate/20"}`}>
                  {p.active ? "Actif" : "Inactif"}
                </button>
                <button type="button" onClick={() => deletePromo(p)} aria-label={`Supprimer ${p.code}`} data-testid={`promo-delete-${p.code}`}
                  className="rounded-lg p-1.5 text-slate/60 transition-colors hover:bg-red-50 hover:text-red-600">
                  <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Compte" testId="settings-account">
        <p className="text-sm text-slate">Rôles &amp; permissions et 2FA : l&apos;architecture est prête (champ rôle + otp_secret). La gestion multi-utilisateurs arrive avec la phase Automatisation.</p>
      </Panel>

      <Panel title="Zone de danger" testId="settings-danger">
        <div className="rounded-xl border border-red-200 bg-red-50/60 p-4">
          <p className="text-sm font-semibold text-red-700">Remise à zéro des données</p>
          <p className="mt-1 text-xs leading-relaxed text-red-600/90">
            Supprime définitivement toutes les réservations, missions, clients, organisations Business,
            devis, factures, paiements, commissions et journaux. Les réglages, la tarification, les chauffeurs,
            les véhicules, les codes promo et les comptes admin sont conservés. Irréversible.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)}
              placeholder="Tapez RESET" data-testid="reset-data-confirm" className={`${inputCls} h-10 w-40 font-mono`} />
            <button type="button" disabled={resetConfirm !== "RESET" || resetting} onClick={resetData}
              data-testid="reset-data-button"
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-red-600 px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40">
              <TriangleAlert className="h-4 w-4" strokeWidth={1.5} />
              {resetting ? "Remise à zéro…" : "Tout supprimer"}
            </button>
          </div>
          {resetResult && (
            <p className="mt-2 text-xs font-medium text-red-700" data-testid="reset-data-result">
              {resetResult} documents supprimés. La plateforme est à zéro.
            </p>
          )}
        </div>
      </Panel>
    </div>
  );
}
